import { AxiosInstance } from "axios";
import { SessionManager } from "../../../auth/sessionManager";
import { saveRawResponse } from "../../../storages/rawResponseStore";
import Cookies from "js-cookie";

// Centralized error format
export type NormalizedError = {
  status: number;
  message: string;
  code?: string | number;
  url?: string;
  method?: string;
  isNetwork?: boolean; // server unreachable / DNS / refused
  isTimeout?: boolean; // request timed out
  raw?: any;
};

export const setupCommonInterceptors = (instance: AxiosInstance) => {
  // Request interceptor -> attach session headers, CSRF, traceId
  instance.interceptors.request.use(async (config) => {
    config.headers = config.headers || {};

    // console.log({config});

    // 1) Attach
    // 1) Attach access token from in-memory session manager (safer than localStorage)
    // const token = SessionManager.getAccessToken() ? SessionManager.getAccessToken() : localStorage.getItem("bbps_token");
    const token =
      SessionManager.getAccessToken() || Cookies.get("bbps_token") || null;

    // console.log("token",token);

    if (token) {
      config.headers = config.headers ?? {};
      config.headers["Authorization"] = `Bearer ${token}`;
      SessionManager.setAccessToken(token);
    }

    // 2) Attach API key or user-specific header if available
    // const apiKey = SessionManager.getUserApiKey();
    // if (apiKey) {
    //   (config.headers as any)["x-user-apikey"] = apiKey;

    //   // 3) Attach CSRF token if present
    //   const csrf = SessionManager.getCsrfToken();
    //   if (csrf) (config.headers as any)["x-csrf-token"] = csrf;

    //   // 4) Add a correlation id for tracing
    //   (config.headers as any)["x-correlation-id"] =
    //     SessionManager.createCorrelationId();
    // }
    return config;
  });

  // Response interceptor -> normalize, persist raw payload, handle refresh
  instance.interceptors.response.use(
    async (response) => {
      // console.log({response});

      // Persist raw response (text/json/xml) centrally for recovery/audit
      try {
        const raw = await response.data; // may be object, string, etc.

        saveRawResponse({
          url: response.config.url || "",
          method: response.config.method || "GET",
          status: response.status,
          timestamp: Date.now(),
          raw,
        });
      } catch (e) {
        // don't break successful flow
        console.warn("failed to persist raw response", e);
      }

      return response;
    },
    async (err) => {
      // console.log({err});

      const cfg = err.config || {};
      const hasResponse = !!err.response;

      // --- Network error (server down / connection refused / DNS) ---
      if (!hasResponse) {
        const isTimeout = err.code === "ECONNABORTED";
        const normalized: NormalizedError = {
          status: 0,
          message: isTimeout
            ? "Request timed out. Please try again."
            : "Please check your internet connection.",
          code: err.code || "internal server error.", //ERR_NETWORK
          url: (cfg.baseURL || "") + (cfg.url || ""),
          method: (cfg.method || "GET").toUpperCase(),
          isNetwork: !isTimeout,
          isTimeout,
          raw: { message: err.message },
        };
        return Promise.reject(normalized);
      }

      // --- HTTP error with response ---
      const normalized: NormalizedError = {
        status: err.response!.status || 500,
        message:
          (err.response!.data &&
            (err.response!.data.message || err.response!.data.error)) ||
          err.message ||
          "Unknown error occurred",
        code: err.code,
        url: (cfg.baseURL || "") + (cfg.url || ""),
        method: (cfg.method || "GET").toUpperCase(),
        raw: err.response?.data ?? null,
      };

      // If 401 -> attempt refresh once per instance using SessionManager queue
      // if (error.response?.status === 401 && !originalRequest?._retry) {
      //   originalRequest._retry = true;
      //   try {
      //     await SessionManager.refreshToken(); // internally queues multiple refresh requests
      //     return instance(originalRequest);
      //   } catch (refreshErr) {
      //     // refresh failed -> force logout (handled by SessionManager)
      //     SessionManager.clearAndRedirectToLogin();
      //     return Promise.reject({ status: 401, message: "Unauthorized" });
      //   }
      // }

      return Promise.reject(normalized);
    },
  );
};
